import { inject, Injectable } from "@angular/core";
import { INavNode } from "@logex/framework/lg-application";
import { SharedUserAuthorizationService } from "@mrdm-self-service-frontend/shared/data-access-authorization";
import { BehaviorSubject, combineLatest } from "rxjs";
import { AdministrationPermissions } from "../authorization";
import { SharedConfigService } from "@mrdm-self-service-frontend/shared/util-logex-framework-setup";

@Injectable({
    providedIn: "root"
})
export class NavigationService {
    private _sharedUserAuthorization = inject(SharedUserAuthorizationService);

    private _configService = inject(SharedConfigService);

    private _navigation = new BehaviorSubject<INavNode[]>(this._navigationWithoutOrgId);
    public navigation$ = this._navigation.asObservable();

    constructor() {
        combineLatest([
            this._sharedUserAuthorization.organizationId$,
            this._sharedUserAuthorization.currentOrganizationPermissions$ // set navigation after current permissions are loaded
        ]).subscribe(([organizationId, _permissions]) => this.setOrgId(organizationId));
    }

    setOrgId(organizationId: number | null): void {
        const navigation =
            organizationId == null
                ? this._navigationWithoutOrgId
                : this._createNavigation(organizationId);

        this._navigation.next(navigation);
    }

    private _createNavigation(organizationId: number): INavNode[] {
        const nav = [
            {
                path: "",
                id: "root",
                noBreadcrumb: true,
                children: [
                    {
                        id: "userNamagement",
                        path: `organization/${organizationId}/account`,
                        lid: "APP._Navigation.UserManagement",
                        anonymous: false
                    },
                    {
                        id: "businessRoles",
                        path: `organization/${organizationId}/business-roles`,
                        lid: "APP._Navigation.BusinessRoles",
                        anonymous: false,
                        permissions: [AdministrationPermissions.admin]
                    },
                    {
                        id: "resources",
                        path: `organization/${organizationId}/resources`,
                        lid: "APP._Navigation.Resources",
                        anonymous: false,
                        hidden: !this._configService.isResourcesEnabled,
                        permissions: [AdministrationPermissions.admin]
                    },
                    {
                        id: "costCenters",
                        path: `organization/${organizationId}/cost-centers`,
                        lid: "APP._Navigation.CostCenters",
                        anonymous: false,
                        hidden: !this._configService.isCostCentersEnabled,
                        permissions: [AdministrationPermissions.admin]
                    },
                    {
                        id: "legalConsent",
                        path: "legal-consent",
                        lid: "APP._Navigation.LegalConsent",
                        noBreadcrumb: true,
                        hidden: true,
                        anonymous: false
                    }
                ]
            }
        ] as INavNode[];

        return nav;
    }

    private get _navigationWithoutOrgId(): INavNode[] {
        const nav = [
            {
                path: "",
                id: "root",
                noBreadcrumb: true,
                children: [
                    {
                        id: "userNamagement",
                        path: "",
                        lid: "APP._Navigation.UserManagement",
                        anonymous: false
                    },
                    {
                        id: "legalConsent",
                        path: "legal-consent",
                        lid: "APP._Navigation.LegalConsent",
                        noBreadcrumb: true,
                        hidden: true,
                        anonymous: false
                    }
                ]
            }
        ] as INavNode[];

        return nav;
    }
}
